import { useQuery } from '@apollo/client';
import { RTEditor, EmailEditor } from '@nile/banyan/src/Components/Editors';
import { GET_ALL_WORK_STATUSES } from '@nile/graphql';
import { TextField } from '@superops/ui-design-system';
import {
  reactSelectInlineError,
  IndicatorSeparator,
  DropdownIndicator,
  statusStylesObject,
  getTranslatedText,
  getCookieValue,
  getHeaderValue,
  TEXT_LENGTH,
  AppContext,
  fetchApi
} from '@nile/components';
import SuperEditor from '@nile/components/src/Components/RichTextEditor/SuperEditor';
import React, { useState, useEffect, useContext } from 'react';
import Select from 'react-select';
import styled from 'styled-components';
import { ProjectDescriptionLoading } from '@nile/projects/src/Components/Loading/Cards';
import { injectIntl, FormattedMessage } from 'react-intl';
import { cloneDeep } from 'lodash';
import envConfig from '@nile/home/src/Config/env';

const OuterWrapper = styled.div`
  display: flex;
  align-items: center;
  .statusHolder {
    display: flex;
    justify-content: flex-end;
    flex: 1 1;
  }
`;

const CardWrapper = styled.div`
  padding-bottom: 40px;
  border-bottom: 1px solid #0000001a;
`;

const ProjectTitle = styled.div`
  flex: 3 3;
  /* padding-left: 10px; */
`;

const EditorHolder = styled.div`
  #quill-rt-editor {
    height: 100%;
    .quill {
      min-height: 200px;
      .ql-container {
        border: none;
        min-height: inherit;
        .ql-editor {
          min-height: inherit;
          padding: 0px;
          &.ql-blank::before {
            left: 0px;
          }
        }
      }
    }
    .ql-toolbar {
      border: none;
      padding: 0px;
    }
  }
`;

const ProjectDescription = (props) => {
  const { intl, hideStatus, meta } = props;
  const appContext = useContext(AppContext);
  const [statuses, setStatuses] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState(props?.errors || {});
  const { loading, error, data } = useQuery(GET_ALL_WORK_STATUSES, {
    onCompleted(newData) {
      setStatuses(
        newData.getWorkStatuses.map((status) => ({
          ...status,
          value: status.name,
          label: status.name
        }))
      );
    }
  });

  useEffect(() => {
    setErrors(props.errors);
  }, [props.errors]);

  if (loading) {
    return <ProjectDescriptionLoading />;
  }

  const handleStatusChange = (val) => {
    props.handleChange(val.value, 'status');
  };

  const getSelectObject = (val) => {
    return statuses.find((item) => item.value === val);
  };

  const handleFileAttachments = (files) => {
    const formData = new FormData();
    // Add files
    files.forEach((file, idx) => {
      formData.append(`files`, file);
    });

    // Add module
    formData.append(
      'module',
      props.attachmentModule || 'PROJECT_CONVERSATION_ATTACHMENT'
    );
    // Add details
    if (props.template?.id) {
      switch (props.attachmentModule) {
        case 'PROJECT_TEMPLATE_ATTACHMENT':
          formData.append(
            'details',
            JSON.stringify({ projectTemplateId: props.template.id })
          );
          break;
        default:
          break;
      }
    }

    setIsUploading(true);
    const requestOptions = {
      method: 'POST',
      credentials: 'include',
      body: formData,
      headers: getHeaderValue()
    };
    fetchApi(`${envConfig.fileRoot}/upload`, requestOptions)
      .then((response) => {
        return response.json();
      })
      .then((response) => response.data)
      .then((list) => {
        const updatedTemplate = cloneDeep(props.template);
        const updatedList = [...updatedTemplate?.attachments, ...(list || [])];
        updatedTemplate.attachments = updatedList;
        props.handleChange(updatedTemplate, 'template');
      })
      .catch((err) => {
        console.error('Error:', err);
      })
      .finally(() => setIsUploading(false));
  };

  const handleAttachmentsDeletion = (fileName) => {
    const updatedTemplate = cloneDeep(props.template);
    const updatedList = cloneDeep(updatedTemplate?.attachments).filter(
      (item) => item.fileName !== fileName
    );
    updatedTemplate.attachments = updatedList;
    props.handleChange(updatedTemplate, 'template');
  };

  return (
    <CardWrapper className="mb40">
      <OuterWrapper>
        <ProjectTitle data-qa="projectInput">
          <TextField
            placeholder={getTranslatedText(intl, meta.titlePlaceholder)}
            value={props.title}
            onChange={(val) => {
              setErrors(null);
              props.handleChange(val, 'title');
            }}
            error={errors?.title}
            maxLength={TEXT_LENGTH.size_5}
            fontSize="fontLarge"
            noborder
          />
        </ProjectTitle>
        {!hideStatus && (
          <div className="statusHolder actr">
            <div className="Polaris-Labelled__LabelWrapper">
              <div className="Polaris-Label">
                <label
                  id="userPhoneLabel"
                  htmlFor="userPhone"
                  className="Polaris-Label__Text"
                >
                  <FormattedMessage id="common.status" />
                </label>
              </div>
            </div>

            <div className="projectStatus" data-qa="projectStatus">
              <Select
                defaultValue={getSelectObject('Planned')}
                components={{
                  IndicatorSeparator,
                  DropdownIndicator
                }}
                className={`${
                  appContext?.darkTheme ? 'dark-reader-react-select' : ''
                }`}
                classNamePrefix={`${
                  appContext?.darkTheme ? 'dark-reader-react-select' : ''
                }`}
                styles={{
                  ...statusStylesObject,
                  valueContainer: (provided, state) => ({
                    ...provided,
                    background: 'var(--sops-light-blue)',
                    textTransform: 'uppercase',
                    borderRadius: '4px',
                    padding: '0px 8px',
                    fontSize: '1.2rem',
                    fontWeight: '700',
                    color: '#00000099',
                    marginLeft: '1rem',
                    marginRight: '1rem',
                    height: '2rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    flex: '0 0 120px'
                  }),
                  control: (provided, state) => ({
                    ...provided,
                    border: 'none',
                    boxShadow: 'none',
                    justifyContent: 'center',
                    flexWrap: 'nowrap'
                  })
                }}
                value={props?.status && getSelectObject(props.status)}
                onChange={handleStatusChange}
                options={statuses}
                isSearchable={false}
              />
            </div>
          </div>
        )}
      </OuterWrapper>
      <EditorHolder data-qa="projectDescription">
        {props.useSuperEditor ? (
          <SuperEditor
            title="Add Attachments"
            description="Upload/drop files here"
            allowMultiple
            toolbarSticky={false}
            config={{
              htmlUntouched: true
            }}
            allowMentions
            storeConfig={props?.editorConfig}
            placeholder={getTranslatedText(intl, 'ticket.newProject.pl.desc')}
            module={props.attachmentModule || 'PROJECT_CONVERSATION_ATTACHMENT'} // To attach inline images
            details={
              props?.client?.accountId
                ? { operation: 'CREATE', clientId: props.client?.accountId }
                : { operation: 'CREATE' }
            } // To attach inline images
            uploadParams={props?.uploadParams} // To upload attachment
            attachmentUploadUrl={`${envConfig.fileRoot}/upload`} // To upload attachment
          />
        ) : (
          <EmailEditor
            template={props?.template}
            updateTemplate={props?.updateTemplate}
            handleFileAttachments={handleFileAttachments}
            handleAttachmentsDeletion={handleAttachmentsDeletion}
            placeholder={getTranslatedText(intl, 'ticket.newProject.pl.desc')}
            module={props.module || 'PROJECT'}
            attachmentModule={
              props.attachmentModule || 'PROJECT_CONVERSATION_ATTACHMENT'
            }
            details={
              props?.client?.accountId
                ? { operation: 'CREATE', clientId: props.client?.accountId }
                : { operation: 'CREATE' }
            }
            allowMentions
            isUploading={isUploading}
          />
        )}
        {errors?.content && reactSelectInlineError(errors.content)}
      </EditorHolder>
    </CardWrapper>
  );
};

export default injectIntl(ProjectDescription);
